import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import * as api from '../api/endpoints';

const EMPTY_ACCOUNT_FORM = { email: '', password: '', first_name: '', last_name: '', address: '', city: '', postcode: '', contact_number: '' };
const EMPTY_ADHOC_FORM = { username: '', email: '', first_name: '', last_name: '', address: '', city: '', postcode: '', contact_number: '' };

/**
 * Search an existing Mavis customer, create a brand-new Mavis account on
 * the spot, or -- for someone who isn't (and may not become) a real Mavis
 * customer, e.g. a one-off enquiry -- log a local-only contact that never
 * touches mavis_backend at all.
 *
 * Props:
 *   - selectedCustomer: { id, mavis_user_id, is_adhoc, username, email, display_name } | null
 *   - onSelect: (customer) => void  -- null to clear
 */
const CustomerPicker = ({ selectedCustomer, onSelect }) => {
  const [mode, setMode] = useState('search'); // 'search' | 'new-account' | 'adhoc'
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [newAccount, setNewAccount] = useState(EMPTY_ACCOUNT_FORM);
  const [adhoc, setAdhoc] = useState(EMPTY_ADHOC_FORM);
  const [isCreating, setIsCreating] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (mode !== 'search' || query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        setIsSearching(true);
        const data = await api.searchCustomers(query.trim());
        setResults(data);
        setShowResults(true);
      } catch (error) {
        console.error('Error searching customers:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, mode]);

  // These fields live inside CreateTicketModal's outer <form>. There's no
  // inner <form> of their own (nested forms cause the submit event to
  // bubble and trigger the outer ticket-creation submit too), so guard
  // against Enter key here submitting that outer form prematurely.
  const preventEnterSubmit = (e) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  const handleSelectResult = (customer) => {
    onSelect(customer);
    setQuery('');
    setResults([]);
    setShowResults(false);
  };

  const handleCreateAccount = async () => {
    if (!newAccount.email.trim() || !newAccount.password) {
      toast.error('Email and password are required.');
      return;
    }
    try {
      setIsCreating(true);
      const created = await api.quickCreateCustomer({
        email: newAccount.email.trim(),
        password: newAccount.password,
        first_name: newAccount.first_name.trim(),
        last_name: newAccount.last_name.trim(),
        address: newAccount.address.trim(),
        city: newAccount.city.trim(),
        postcode: newAccount.postcode.trim(),
        contact_number: newAccount.contact_number.trim(),
      });
      toast.success('Customer account created on Mavis.');
      onSelect(created);
      setMode('search');
      setNewAccount(EMPTY_ACCOUNT_FORM);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create customer.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateAdhoc = async () => {
    if (!adhoc.username.trim()) {
      toast.error('A name is required.');
      return;
    }
    try {
      setIsCreating(true);
      const created = await api.createAdhocCustomer({
        username: adhoc.username.trim(),
        email: adhoc.email.trim(),
        first_name: adhoc.first_name.trim(),
        last_name: adhoc.last_name.trim(),
        address: adhoc.address.trim(),
        city: adhoc.city.trim(),
        postcode: adhoc.postcode.trim(),
        contact_number: adhoc.contact_number.trim(),
      });
      toast.success('One-off contact added to this ticket.');
      onSelect(created);
      setMode('search');
      setAdhoc(EMPTY_ADHOC_FORM);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add contact.');
    } finally {
      setIsCreating(false);
    }
  };

  if (selectedCustomer) {
    return (
      <div className="customer-picker">
        <div className="form-input" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>
            {selectedCustomer.display_name || selectedCustomer.username}
            {selectedCustomer.email && <> &lt;{selectedCustomer.email}&gt;</>}
            {selectedCustomer.is_adhoc && <span className="label-chip" style={{ marginLeft: '0.5rem' }}>One-off contact</span>}
          </span>
          <button type="button" className="btn-secondary" onClick={() => onSelect(null)}>Change</button>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-picker">
      <div className="segmented-control">
        <button type="button" className={mode === 'search' ? 'active' : ''} onClick={() => setMode('search')}>Search existing</button>
        <button type="button" className={mode === 'new-account' ? 'active' : ''} onClick={() => setMode('new-account')}>New Mavis account</button>
        <button type="button" className={mode === 'adhoc' ? 'active' : ''} onClick={() => setMode('adhoc')}>One-off contact</button>
      </div>

      {mode === 'search' && (
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            className="form-input"
            placeholder="Search customer by name, username, or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setShowResults(true)}
            aria-label="Search for a customer"
            role="combobox"
            aria-expanded={showResults && results.length > 0}
            aria-controls="customer-results-listbox"
            aria-autocomplete="list"
          />
          {showResults && query.trim().length >= 2 && (
            <ul id="customer-results-listbox" role="listbox" className="customer-results">
              {isSearching && <li>Searching...</li>}
              {!isSearching && results.length === 0 && <li style={{ color: '#aab7c4' }}>No matching customers</li>}
              {!isSearching && results.map((customer) => (
                <li
                  key={customer.id}
                  role="option"
                  aria-selected={false}
                  tabIndex={0}
                  onClick={() => handleSelectResult(customer)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectResult(customer);
                    }
                  }}
                >
                  {customer.display_name} &lt;{customer.email}&gt;
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {mode === 'new-account' && (
        <div onKeyDown={preventEnterSubmit} style={{ padding: '0.75rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px' }}>
          <p style={{ fontSize: '0.8rem', color: '#aab7c4', marginTop: 0 }}>Creates a real account on Mavis with login credentials. The account logs in with its email -- there's no separate username.</p>
          <div className="form-group">
            <label className="form-label" htmlFor="nc-email">Email <span className="required">*</span></label>
            <input id="nc-email" type="email" className="form-input" value={newAccount.email}
              onChange={(e) => setNewAccount((p) => ({ ...p, email: e.target.value }))} disabled={isCreating} />
          </div>
          <div className="field-row">
            <div className="form-group">
              <label className="form-label" htmlFor="nc-first">First name</label>
              <input id="nc-first" className="form-input" value={newAccount.first_name}
                onChange={(e) => setNewAccount((p) => ({ ...p, first_name: e.target.value }))} disabled={isCreating} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="nc-last">Last name</label>
              <input id="nc-last" className="form-input" value={newAccount.last_name}
                onChange={(e) => setNewAccount((p) => ({ ...p, last_name: e.target.value }))} disabled={isCreating} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="nc-address">Address</label>
            <input id="nc-address" className="form-input" value={newAccount.address}
              onChange={(e) => setNewAccount((p) => ({ ...p, address: e.target.value }))} disabled={isCreating} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="nc-city">City</label>
            <input id="nc-city" className="form-input" value={newAccount.city}
              onChange={(e) => setNewAccount((p) => ({ ...p, city: e.target.value }))} disabled={isCreating} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="nc-postcode">Postcode</label>
            <input id="nc-postcode" className="form-input" value={newAccount.postcode}
              onChange={(e) => setNewAccount((p) => ({ ...p, postcode: e.target.value }))} disabled={isCreating} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="nc-contact">Contact number</label>
            <input id="nc-contact" className="form-input" maxLength={15} value={newAccount.contact_number}
              onChange={(e) => setNewAccount((p) => ({ ...p, contact_number: e.target.value }))} disabled={isCreating} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="nc-password">Password <span className="required">*</span></label>
            <input id="nc-password" type="password" className="form-input" value={newAccount.password}
              onChange={(e) => setNewAccount((p) => ({ ...p, password: e.target.value }))} disabled={isCreating} />
          </div>
          <button type="button" className="btn-primary" onClick={handleCreateAccount} disabled={isCreating}>
            {isCreating ? 'Creating...' : 'Create customer account'}
          </button>
        </div>
      )}

      {mode === 'adhoc' && (
        <div onKeyDown={preventEnterSubmit} style={{ padding: '0.75rem', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px' }}>
          <p style={{ fontSize: '0.8rem', color: '#aab7c4', marginTop: 0 }}>
            Just for this ticket -- no Mavis account or login is created, nothing is sent to the customer database.
          </p>
          <div className="field-row">
            <div className="form-group">
              <label className="form-label" htmlFor="ah-name">Name <span className="required">*</span></label>
              <input id="ah-name" className="form-input" value={adhoc.username}
                onChange={(e) => setAdhoc((p) => ({ ...p, username: e.target.value }))} disabled={isCreating} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="ah-email">Email</label>
              <input id="ah-email" type="email" className="form-input" value={adhoc.email}
                onChange={(e) => setAdhoc((p) => ({ ...p, email: e.target.value }))} disabled={isCreating} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="ah-address">Address</label>
            <input id="ah-address" className="form-input" value={adhoc.address}
              onChange={(e) => setAdhoc((p) => ({ ...p, address: e.target.value }))} disabled={isCreating} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="ah-city">City</label>
            <input id="ah-city" className="form-input" value={adhoc.city}
              onChange={(e) => setAdhoc((p) => ({ ...p, city: e.target.value }))} disabled={isCreating} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="ah-postcode">Postcode</label>
            <input id="ah-postcode" className="form-input" value={adhoc.postcode}
              onChange={(e) => setAdhoc((p) => ({ ...p, postcode: e.target.value }))} disabled={isCreating} />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="ah-contact">Contact number</label>
            <input id="ah-contact" className="form-input" maxLength={15} value={adhoc.contact_number}
              onChange={(e) => setAdhoc((p) => ({ ...p, contact_number: e.target.value }))} disabled={isCreating} />
          </div>
          <button type="button" className="btn-primary" onClick={handleCreateAdhoc} disabled={isCreating}>
            {isCreating ? 'Adding...' : 'Add contact'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomerPicker;
